import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useInView } from 'framer-motion';

const VIEW_WIDTH = 520;
const TOP = 10;
const ROW_GAP = 96;
const NODE_HEIGHT = 52;
const LABEL_SIZE = 14;
const SUBLABEL_SIZE = 11;

// Same pacing as the CTD flow diagram: a node appears, its lines fade in with their arrows, then the next nodes.
const NODE_MS = 67;
const LINE_MS = 133;
const GAP_MS = 33;

const PairGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin: 3.5rem 0 1rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const GraphTitle = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  margin-bottom: 1.25rem;
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
`;

// Width is estimated from character counts because SVG text cannot size its own box.
const nodeWidth = (node) =>
  Math.max(node.label.length * 8, (node.sublabel || '').length * 6, 70) + 28;

const layout = (nodes, rows) => {
  const byRow = new Map();
  nodes.forEach((node) => {
    if (!byRow.has(node.row)) byRow.set(node.row, []);
    byRow.get(node.row).push(node);
  });
  // A graph with fewer rows than its neighbour spreads them over the same height, so the pair lines up.
  const ownRows = Math.max(...nodes.map((n) => n.row)) + 1;
  const rowGap = ownRows > 1 ? ((rows - 1) * ROW_GAP) / (ownRows - 1) : 0;
  const placed = {};
  byRow.forEach((rowNodes, row) => {
    const slot = VIEW_WIDTH / rowNodes.length;
    rowNodes.forEach((node, i) => {
      const width = Math.min(nodeWidth(node), slot - 10);
      placed[node.id] = {
        ...node,
        width,
        cx: slot * (i + 0.5),
        y: TOP + row * rowGap,
      };
    });
  });
  return placed;
};

// Returns the path plus its end point and direction, so the arrowhead sits on the line's tip.
const edgeGeometry = (from, to) => {
  if (from.row === to.row) {
    const leftToRight = from.cx < to.cx;
    const y = from.y + NODE_HEIGHT / 2;
    const x1 = from.cx + (leftToRight ? from.width / 2 : -from.width / 2);
    const x2 = to.cx + (leftToRight ? -to.width / 2 - 2 : to.width / 2 + 2);
    return { d: `M ${x1} ${y} L ${x2} ${y}`, tip: { x: x2, y }, dir: leftToRight ? 'right' : 'left' };
  }
  const x1 = from.cx;
  const y1 = from.y + NODE_HEIGHT;
  const x2 = to.cx;
  const y2 = to.y - 2;
  const bend = (y2 - y1) * 0.5;
  return {
    d: `M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2}`,
    tip: { x: x2, y: y2 },
    dir: 'down',
  };
};

const arrowPoints = ({ x, y }, dir) => {
  if (dir === 'right') return `${x},${y} ${x - 8},${y - 4.5} ${x - 8},${y + 4.5}`;
  if (dir === 'left') return `${x},${y} ${x + 8},${y - 4.5} ${x + 8},${y + 4.5}`;
  return `${x},${y} ${x - 4.5},${y - 8} ${x + 4.5},${y - 8}`;
};

// Orders the reveal by dependency: a node appears once every line into it has faded in.
const buildTimings = (nodes, edges) => {
  const incoming = {};
  edges.forEach((e) => {
    (incoming[e.to] = incoming[e.to] || []).push(e);
  });
  const key = (e) => `${e.from}-${e.to}`;
  const nodeAt = {};
  const edgeAt = {};
  const roots = nodes.filter((n) => !incoming[n.id]).map((n) => n.id);
  roots.forEach((id) => { nodeAt[id] = 0; });
  const done = new Set(roots);
  let t = NODE_MS + GAP_MS;
  let guard = 0;
  while (done.size < nodes.length && guard++ < 30) {
    const lineStart = t;
    const lines = edges.filter((e) => done.has(e.from) && edgeAt[key(e)] === undefined);
    if (!lines.length) break;
    lines.forEach((e) => { edgeAt[key(e)] = lineStart; });
    t += LINE_MS + GAP_MS;
    const nodeStart = lineStart + LINE_MS - NODE_MS * 0.2;
    const ready = nodes.filter(
      (n) => !done.has(n.id) && (incoming[n.id] || []).every((e) => edgeAt[key(e)] !== undefined)
    );
    ready.forEach((n) => { nodeAt[n.id] = nodeStart; done.add(n.id); });
    if (ready.length) t += NODE_MS + GAP_MS;
  }
  // Lines into the same node arrive together, and the node waits for the last of them.
  Object.keys(incoming).forEach((target) => {
    const latest = Math.max(...incoming[target].map((e) => edgeAt[key(e)] ?? 0));
    incoming[target].forEach((e) => { edgeAt[key(e)] = latest; });
    nodeAt[target] = Math.max(nodeAt[target] ?? 0, latest + LINE_MS - NODE_MS * 0.2);
  });
  return { nodeAt, edgeAt };
};

export const ArchitectureGraph = ({ graph, rows }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  const rowCount = rows || Math.max(...graph.nodes.map((n) => n.row)) + 1;
  const placed = useMemo(() => layout(graph.nodes, rowCount), [graph.nodes, rowCount]);
  const timings = useMemo(() => buildTimings(graph.nodes, graph.edges), [graph.nodes, graph.edges]);
  const height = TOP * 2 + (rowCount - 1) * ROW_GAP + NODE_HEIGHT + 4;

  // A node lifts a few pixels along the line that feeds it; a node nothing feeds rises into place.
  const entryOffset = (id) => {
    const feed = graph.edges.find((e) => e.to === id);
    if (!feed) return 'translate(0px, 6px)';
    const from = placed[feed.from];
    const to = placed[id];
    if (from.row === to.row) return `translate(${from.cx < to.cx ? -6 : 6}px, 0px)`;
    return 'translate(0px, -6px)';
  };

  return (
    <div ref={ref}>
      <GraphTitle>{graph.title}</GraphTitle>
      <Svg viewBox={`0 0 ${VIEW_WIDTH} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${graph.title} architecture`}>
        {graph.edges.map((edge) => {
          const from = placed[edge.from];
          const to = placed[edge.to];
          if (!from || !to) return null;
          const k = `${edge.from}-${edge.to}`;
          const reveal = {
            opacity: inView ? 0.6 : 0,
            transition: `opacity ${LINE_MS}ms ease-out ${timings.edgeAt[k] ?? 0}ms`,
          };
          const { d, tip, dir } = edgeGeometry(from, to);
          return (
            <g key={k} style={reveal}>
              <path d={d} fill="none" stroke="#4A4A4A" strokeWidth="1.3" strokeLinecap="round" />
              <polygon points={arrowPoints(tip, dir)} fill="#4A4A4A" />
            </g>
          );
        })}

        {Object.values(placed).map((node) => {
          const x = node.cx - node.width / 2;
          const at = timings.nodeAt[node.id] ?? 0;
          return (
            <g
              key={node.id}
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translate(0px, 0px)' : entryOffset(node.id),
                transition: `opacity ${NODE_MS}ms ease-out ${at}ms, transform ${NODE_MS}ms ease-out ${at}ms`,
              }}
            >
              <rect
                x={x}
                y={node.y}
                width={node.width}
                height={NODE_HEIGHT}
                rx="12"
                fill="#FFFFFF"
                stroke={node.accent ? 'rgba(0, 102, 255, 0.55)' : 'rgba(0, 0, 0, 0.14)'}
                strokeWidth="1.5"
              />
              <text
                x={node.cx}
                y={node.y + (node.sublabel ? 22 : NODE_HEIGHT / 2 + 4.5)}
                textAnchor="middle"
                style={{ fontFamily: 'var(--font-primary)', fontSize: LABEL_SIZE, fontWeight: 600, fill: 'var(--text-secondary)', letterSpacing: '-0.01em' }}
              >
                {node.label}
              </text>
              {node.sublabel && (
                <text
                  x={node.cx}
                  y={node.y + 38}
                  textAnchor="middle"
                  style={{ fontFamily: 'var(--font-primary)', fontSize: SUBLABEL_SIZE, fill: 'var(--text-muted)' }}
                >
                  {node.sublabel}
                </text>
              )}
            </g>
          );
        })}
      </Svg>
    </div>
  );
};

export const ArchitecturePair = ({ graphs }) => {
  const rows = Math.max(...graphs.flatMap((g) => g.nodes.map((n) => n.row))) + 1;
  return (
    <PairGrid>
      {graphs.map((graph) => (
        <ArchitectureGraph key={graph.title} graph={graph} rows={rows} />
      ))}
    </PairGrid>
  );
};

export default ArchitectureGraph;
